import React from "react"

import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceArea,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"

import { DIVIDER_STROKE } from "./chartTheme"
import { PRIMARY_SERIES_COLOR } from "./seriesPalette"

interface TimeSeriesPoint {
  t: number
  value: number | null
  // Extra fields are passed through to the tooltip formatter.
  [key: string]: number | string | null | undefined
}

interface TimeSeriesHighlight {
  start: number
  end: number
  fill: string // rgb triple, e.g. "245, 158, 11"
  opacity?: number
}

interface TimeSeriesMarker {
  x: number
  y: number
  /** Short glyph drawn at (x, y), e.g. an arrow. */
  glyph: string
  /** CSS color for the glyph. */
  color?: string
}

interface TimeSeriesLineChartProps {
  data: TimeSeriesPoint[]
  /** Upper bound of the x domain. Defaults to the last point's t. */
  maxX?: number
  /** Tailwind height class for the container. */
  heightClassName?: string
  /** x-axis tick + tooltip-label formatter. */
  formatX?: (x: number) => string
  /** y-axis tick formatter. */
  formatY?: (y: number) => string
  yAxisLabel?: string
  highlights?: TimeSeriesHighlight[]
  /** Horizontal reference lines (e.g. a zero baseline). */
  referenceLines?: number[]
  /** Glyph markers drawn at (x, y) in data coords (e.g. expected-value annotations). */
  markers?: TimeSeriesMarker[]
  /** x position (same units as the data's `t`) of a vertical playhead line; null hides it. */
  playheadX?: number | null
  /** Tooltip value text for a point; defaults to the formatted y value. */
  formatTooltipValue?: (point: TimeSeriesPoint) => string
  tooltipLabel?: string
  lineColor?: string
  connectNulls?: boolean
  /** Called with the x (time) position of a click on the plot. */
  onPointClick?: (x: number) => void
  /** Stop the click bubbling (needed inside MetricGrid deep dives). */
  stopClickPropagation?: boolean
  ariaLabel?: string
}

const defaultFormatY = (y: number): string => `${Math.round(y)}`

// Generic value-over-time line chart: configurable axes/tooltip, optional
// highlighted regions, reference lines, and click-to-seek. Reusable anywhere
// (deep dives, dashboards, standalone panels) — keep it free of domain logic.
export function TimeSeriesLineChart({
  data,
  maxX,
  heightClassName = "h-40",
  formatX = (x) => `${x}`,
  formatY = defaultFormatY,
  yAxisLabel,
  highlights = [],
  referenceLines = [],
  markers = [],
  formatTooltipValue,
  tooltipLabel = "Value",
  lineColor = PRIMARY_SERIES_COLOR,
  connectNulls = false,
  onPointClick,
  stopClickPropagation = false,
  ariaLabel,
  playheadX = null,
}: TimeSeriesLineChartProps): React.ReactElement {
  const domainMax =
    maxX ?? (data.length ? Math.ceil(data[data.length - 1].t) : 0)

  const chart = (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart
        data={data}
        margin={{ top: 5, right: 8, bottom: 0, left: -12 }}
        onClick={
          onPointClick
            ? (state) => {
                const x = Number(state?.activeLabel)
                if (Number.isFinite(x)) {
                  onPointClick(x)
                }
              }
            : undefined
        }
      >
        <CartesianGrid
          stroke={DIVIDER_STROKE}
          strokeDasharray="3 3"
          vertical={false}
        />
        <XAxis
          dataKey="t"
          type="number"
          domain={[0, domainMax]}
          tickFormatter={formatX}
          tick={{ fontSize: 10 }}
          stroke={DIVIDER_STROKE}
        />
        <YAxis
          width={40}
          tick={{ fontSize: 10 }}
          stroke={DIVIDER_STROKE}
          tickFormatter={formatY}
          label={
            yAxisLabel
              ? {
                  value: yAxisLabel,
                  angle: -90,
                  position: "insideLeft",
                  style: {
                    fontSize: 10,
                    fill: "hsl(var(--heroui-default-400))",
                  },
                }
              : undefined
          }
        />
        <Tooltip
          formatter={(value, _name, item) => {
            const point = item?.payload as TimeSeriesPoint | undefined
            const text =
              formatTooltipValue && point
                ? formatTooltipValue(point)
                : typeof value === "number"
                  ? formatY(value)
                  : "—"
            return [text, tooltipLabel]
          }}
          labelFormatter={(label) => formatX(Number(label))}
          contentStyle={{ fontSize: 11 }}
        />
        {referenceLines.map((y) => (
          <ReferenceLine
            key={y}
            y={y}
            stroke={DIVIDER_STROKE}
            strokeDasharray="2 2"
          />
        ))}
        {highlights.map((h) => (
          <ReferenceArea
            key={`${h.fill}:${h.start}`}
            x1={h.start}
            x2={h.end}
            fill={`rgba(${h.fill}, ${h.opacity ?? 0.18})`}
            stroke="none"
          />
        ))}
        {markers.map((marker) => (
          <ReferenceDot
            key={`${marker.x}:${marker.y}:${marker.glyph}`}
            x={marker.x}
            y={marker.y}
            r={0}
            ifOverflow="extendDomain"
            label={{
              value: marker.glyph,
              position: "center",
              fontSize: 16,
              fontWeight: 700,
              fill: marker.color ?? "var(--coval-success-border)",
            }}
          />
        ))}
        {playheadX != null && playheadX >= 0 && playheadX <= domainMax && (
          <ReferenceLine
            x={playheadX}
            stroke="var(--coval-danger-border)"
            strokeWidth={1.5}
            ifOverflow="hidden"
          />
        )}
        <Line
          type="monotone"
          dataKey="value"
          stroke={lineColor}
          strokeWidth={1.5}
          dot={false}
          connectNulls={connectNulls}
          isAnimationActive={false}
        />
      </LineChart>
    </ResponsiveContainer>
  )

  if (onPointClick) {
    return (
      <div
        className={`${heightClassName} w-full cursor-pointer`}
        role="button"
        tabIndex={0}
        aria-label={ariaLabel}
        onClick={stopClickPropagation ? (e) => e.stopPropagation() : undefined}
      >
        {chart}
      </div>
    )
  }

  return <div className={`${heightClassName} w-full`}>{chart}</div>
}
